// app_client/src/App.jsx
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAutoUpdate from './hooks/useAutoUpdate';
import useAuthStore from './store/authStore';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UserManagement from './pages/admin/UserManagement';
import RoleManagement from './pages/admin/RoleManagement';
import AccountDetail from './pages/admin/AccountDetail';
import AccountManagement from './pages/admin/AccountManagement';
import FinancialAccountManagement from './pages/admin/FinancialAccountManagement';
import SystemSettings from './pages/admin/SystemSettings';
import AuditLogs from './pages/admin/AuditLogs';
import Notifications from './pages/admin/Notifications';
import DocumentSettings from './pages/admin/DocumentSettings';
import CompanyInfo from './pages/admin/CompanyInfo';
import TransactionManagement from './pages/admin/TransactionManagement';
import ProductDetail from './pages/admin/ProductDetail';
import ProductManagement from './pages/admin/ProductManagement';
import InvoiceDetail from './pages/admin/InvoiceDetail';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import ModuleManagement from './pages/admin/ModuleManagement';
import Dashboard from './pages/admin/Dashboard';
import OperationManagement from './pages/admin/OperationManagement';
import OperationCreate from './pages/admin/OperationCreate';
import OperationDetail from './pages/admin/OperationDetail';
import PersonnelManagement from './pages/admin/PersonnelManagement';
import PersonnelCreate from './pages/admin/PersonnelCreate';
import PersonnelDetail from './pages/admin/PersonnelDetail';
import ConfirmationProvider from './components/providers/ConfirmationProvider';
import ErrorProvider from './components/providers/ErrorProvider';

// Layout ve Sayfalar
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login';

// Sayfa Bileşenleri (Şimdilik Placeholder, ileride dosyalara taşıyacağız)
const Storage = () => <div className="p-6"><h1>Depo & Konteyner</h1></div>;
const Vehicles = () => <div className="p-6"><h1>Araç Takip</h1></div>;

function AutoUpdate() {
  useAutoUpdate();
  return null;
}

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <BrowserRouter>
      <AutoUpdate />
      <ErrorProvider>
        <ConfirmationProvider>
          <Toaster 
            position="top-right"
            reverseOrder={false}
            toastOptions={{
              // Global stiller
              duration: 3000,
              style: {
                background: '#333',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '12px',
              },
              success: {
                theme: {
                  primary: '#10b981',
                },
              },
            }} 
          />
          <Routes>
            {/* Giriş Sayfası */}
            <Route path="/login" element={<Login />} />

            {/* Ana Uygulama Rotaları */}
            <Route 
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<ProtectedRoute requiredModule="dashboard"><Dashboard /></ProtectedRoute>} />

              <Route path="/accounts" element={<ProtectedRoute requiredModule="account"><AccountManagement /></ProtectedRoute>} />
              <Route path="/accounts/new" element={<ProtectedRoute requiredModule="account" requiredAction="write"><AccountDetail /></ProtectedRoute>} />
              <Route path="/accounts/:id" element={<ProtectedRoute requiredModule="account"><AccountDetail /></ProtectedRoute>} />
              <Route path="/accounts/:id/edit" element={<ProtectedRoute requiredModule="account" requiredAction="write"><AccountDetail /></ProtectedRoute>} />

              <Route path="/financial-accounts" element={<ProtectedRoute requiredModule="financialaccounts"><FinancialAccountManagement /></ProtectedRoute>} />

              <Route path="/transactions" element={<ProtectedRoute requiredModule="transaction"><TransactionManagement /></ProtectedRoute>} />
              <Route path="/notifications" element={<Notifications />} />

              <Route path="/operations" element={<ProtectedRoute><OperationManagement /></ProtectedRoute>} />
              <Route path="/operations/new" element={<ProtectedRoute><OperationCreate /></ProtectedRoute>} />
              <Route path="/operations/:id" element={<ProtectedRoute><OperationDetail /></ProtectedRoute>} />
              <Route path="/personnel" element={<ProtectedRoute requiredModule="account"><PersonnelManagement /></ProtectedRoute>} />
              <Route path="/personnel/new" element={<ProtectedRoute requiredModule="account" requiredAction="write"><PersonnelCreate /></ProtectedRoute>} />
              <Route path="/personnel/:id" element={<ProtectedRoute requiredModule="account"><PersonnelDetail /></ProtectedRoute>} />

              <Route path="/storage" element={<Storage />} />
              <Route path="/vehicles" element={<Vehicles />} />

              <Route path="/admin/system">
                <Route index element={<Navigate to="/admin/system/settings" replace />} />
                <Route path="settings" element={<ProtectedRoute requiredModule="system"><SystemSettings /></ProtectedRoute>} />
                <Route path="company-info" element={<ProtectedRoute requiredModules={['system', 'documents']}><CompanyInfo /></ProtectedRoute>} />
                <Route path="documents" element={<ProtectedRoute requiredModule="documents"><DocumentSettings /></ProtectedRoute>} />
                <Route path="audit-logs" element={<ProtectedRoute requiredModule="system" allowedRoles={['admin']}><AuditLogs /></ProtectedRoute>} />
                <Route path="users" element={<ProtectedRoute requiredModule="users" allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
                <Route path="roles" element={<ProtectedRoute requiredModule="roles" allowedRoles={['admin']}><RoleManagement /></ProtectedRoute>} />
                <Route path="modules" element={<ProtectedRoute requiredModule="modulemanagement" allowedRoles={['admin']}><ModuleManagement /></ProtectedRoute>} />
              </Route>

              <Route path="/products" element={<ProtectedRoute requiredModule="product"><ProductManagement /></ProtectedRoute>} />
              <Route path="/product-management" element={<ProtectedRoute requiredModule="product"><ProductManagement /></ProtectedRoute>} />
              <Route path="/products/new" element={<ProtectedRoute requiredModule="product" requiredAction="write"><ProductDetail /></ProtectedRoute>} />
              <Route path="/products/:id" element={<ProtectedRoute requiredModule="product"><ProductDetail /></ProtectedRoute>} />

              <Route path="/invoices" element={<ProtectedRoute requiredModule="invoice"><InvoiceManagement /></ProtectedRoute>} />
              <Route path="/invoice-management" element={<ProtectedRoute requiredModule="invoice"><InvoiceManagement /></ProtectedRoute>} />
              <Route path="/invoices/new" element={<ProtectedRoute requiredModule="invoice" requiredAction="write"><InvoiceDetail /></ProtectedRoute>} />
              <Route path="/invoices/:id" element={<ProtectedRoute requiredModule="invoice"><InvoiceDetail /></ProtectedRoute>} />
              <Route path="/invoices/:id/edit" element={<ProtectedRoute requiredModule="invoice" requiredAction="write"><InvoiceDetail /></ProtectedRoute>} />

            </Route>

            {/* 404 Yönlendirmesi */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ConfirmationProvider>
      </ErrorProvider>
    </BrowserRouter>
  );
}

export default App;
